import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Skeleton,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { withCommas } from "./numberFormat";

const STRATEGY_ORDER = [
  "MERGER ARBITRAGE",
  "SPAC LIQUIDATION ARBITRAGE",
  "EVENT DRIVEN FIXED INCOME",
  "CONVERTIBLE ARBITRAGE",
  "CREDIT ARBITRAGE",
  "STAT ARBITRAGE",
  "VOL ARBITRAGE",
];

const STRATEGY_SHORT = {
  "MERGER ARBITRAGE": "MERGER ARB",
  "SPAC LIQUIDATION ARBITRAGE": "SPAC LQD ARB",
  "CONVERTIBLE ARBITRAGE": "CVT ARB",
  "CREDIT ARBITRAGE": "CREDIT ARB",
  "EVENT DRIVEN FIXED INCOME": "EDFI ARB",
  "STAT ARBITRAGE": "STAT ARB",
  "VOL ARBITRAGE": "VOL ARB",
};

const HIST_DEFINITIONS = [
  { scenario: "GFC", period: "Sep 12, 2008 – Oct 10, 2008", spx: "-28.2%", vix: "+173%", ig: "+237", hy: "+682", twoY: "-57" },
  { scenario: "Euro Crisis", period: "Jul 22, 2011 – Aug 08, 2011", spx: "-16.8%", vix: "+174%", ig: "+24", hy: "+149", twoY: "-13" },
  { scenario: "China Shock", period: "Aug 17, 2015 – Aug 24, 2015", spx: "-10.0%", vix: "+213%", ig: "+8", hy: "+56", twoY: "-14" },
  { scenario: "Volmageddon", period: "Jan 26, 2018 – Feb 05, 2018", spx: "-7.8%", vix: "+237%", ig: "-1", hy: "+25", twoY: "-9" },
  { scenario: "COVID", period: "Feb 19, 2020 – Mar 23, 2020", spx: "-33.9%", vix: "+328%", ig: "+299", hy: "+730", twoY: "-111" },
  { scenario: "Inflation Shock", period: "Jan 03, 2022 – Oct 12, 2022", spx: "-25.4%", vix: "+102%", ig: "+74", hy: "+226", twoY: "+352" },
];

const CUSTOM_DEFINITIONS = [
  { scenario: "Equity Selloff + Credit Stress", factors: "SPX -10.0%  VIX +12%  HY +200bps" },
  { scenario: "Vol Crush", factors: "VIX -30%" },
  { scenario: "Credit Stress", factors: "IG +150bps  HY +400bps" },
  { scenario: "Rates Rise", factors: "2Y +100bps" },
  { scenario: "Rates Fall", factors: "2Y -100bps" },
  { scenario: "Equity Rally + Credit Rally", factors: "SPX +10.0%  VIX -10%  IG -50bps  2Y -100bps" },
];

// Stable empty default so the grouping memos don't re-run every render.
const NO_GROUPS = [];


function formatNumberish(value) {
  if (value == null || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (Number.isInteger(value)) return String(value);
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatCurrency(value) {
  if (value == null || isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1e6) return `$${withCommas(value / 1e6)}M`;
  if (abs >= 1e3) return `$${withCommas(value / 1e3)}K`;
  return `$${value.toFixed(0)}`;
}

function formatBps(v) {
  if (v == null || isNaN(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)} bps`;
}

function formatGmv(value) {
  if (value == null || isNaN(value) || value === 0) return "—";
  return `$${(value / 1e9).toFixed(2)}B`;
}

function rowBgColor(pnl) {
  if (pnl == null) return "transparent";
  if (pnl < 0) return "rgba(197, 48, 48, 0.05)";
  if (pnl > 0) return "rgba(0, 104, 66, 0.05)";
  return "transparent";
}

function pnlColor(pnl) {
  if (pnl == null) return "inherit";
  if (pnl < 0) return "#d57575";
  if (pnl > 0) return "#5a9c82";
  return "inherit";
}

// On row hover, numbers jump to a bolder/darker red or green and the
// scenario name bolds up, so the lighter resting palette doesn't read as
// washed-out once the user is actually inspecting a row.
const ROW_HOVER_SX = {
  "&:hover .scenario-name": {
    fontWeight: 800,
  },
  "&:hover .pnl-value-neg": {
    color: "#b71c1c !important",
    fontWeight: "900 !important",
    fontSize: "0.82rem !important",
  },
  "&:hover .pnl-value-pos": {
    color: "#00441f !important",
    fontWeight: "900 !important",
    fontSize: "0.82rem !important",
  },
};

// Forces the value tooltip to sit directly below the number instead of
// letting Popper auto-flip it to the side to dodge the scrollable table's
// right/left edges.
const VALUE_TOOLTIP_PROPS = {
  placement: "bottom",
  slotProps: {
    popper: {
      modifiers: [
        { name: "flip", enabled: false },
        { name: "preventOverflow", options: { boundary: "viewport" } },
      ],
    },
  },
};

function pnlHoverClass(pnl) {
  if (pnl == null) return "";
  if (pnl < 0) return "pnl-value-neg";
  if (pnl > 0) return "pnl-value-pos";
  return "";
}

const headerSx = {
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#006842",
  whiteSpace: "normal",
  py: 0.75,
  px: 1,
  borderBottom: "2px solid #006842",
};

const cellSx = {
  fontSize: "0.78rem",
  py: 0.5,
  px: 1,
  borderBottom: "1px solid",
  borderColor: "grey.100",
};

function PnlBpsCell({ pnl, bps, showBpsOfNav }) {
  if (showBpsOfNav) {
    return (
      <Typography
        className={pnlHoverClass(bps)}
        sx={{ fontSize: "0.76rem", fontWeight: 600, color: pnlColor(bps), fontFamily: "monospace", lineHeight: 1.15 }}
      >
        {formatBps(bps)}
      </Typography>
    );
  }
  return (
    <Typography
      className={pnlHoverClass(pnl)}
      sx={{ fontSize: "0.76rem", fontWeight: 600, color: pnlColor(pnl), fontFamily: "monospace", lineHeight: 1.15 }}
    >
      {formatCurrency(pnl)}
    </Typography>
  );
}

export function ScenarioTable({
  rows,
  onSimClick,
  singleStrategy,
  showBpsOfNav,
  expanded = false,
  extraColumns = [],
  visibleRows,
  groupScenarios = false,
  scenarioGroups = NO_GROUPS,
  expandedGroups = {},
  onToggleGroup = () => {},
  sortConfig = { key: null, direction: "asc" },
  onSort = () => {},
  pinRow = null,
}) {
  const strategies = useMemo(() => {
    if (singleStrategy) return [];
    const found = new Set();
    (rows || []).forEach((r) => {
      Object.keys(r.by_strategy || {}).forEach((s) => {
        if (s != null && s !== "null" && s !== "undefined") found.add(s);
      });
    });
    const known = STRATEGY_ORDER.filter((s) => found.has(s));
    const rest = [...found].filter((s) => !STRATEGY_ORDER.includes(s)).sort();
    return [...known, ...rest];
  }, [rows, singleStrategy]);

  // Rows shown above all factor groups (e.g. Base in portfolio greeks), unsorted.
  const pinnedRows = useMemo(
    () => (groupScenarios && pinRow ? (rows || []).filter(pinRow) : []),
    [rows, groupScenarios, pinRow],
  );

  // Group keys and labels come from the backend (scenario_groups, in display order).
  const groupLabelByKey = useMemo(
    () => Object.fromEntries(scenarioGroups.map((g) => [g.key, g.label])),
    [scenarioGroups],
  );

  // Group scenarios if requested
  const groupedRows = useMemo(() => {
    if (!groupScenarios || !rows) return { ungrouped: rows || [] };

    const groups = {};
    scenarioGroups.forEach((g) => { groups[g.key] = []; });

    rows.forEach((row) => {
      if (pinnedRows.includes(row)) return;
      const key = row.group || "UNGROUPED";
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    return groups;
  }, [rows, groupScenarios, pinnedRows, scenarioGroups]);

  // Sort rows within each group
  const sortedGroupedRows = useMemo(() => {
    if (!groupScenarios) {
      if (!sortConfig.key) return { ungrouped: rows || [] };

      const sorted = [...(rows || [])].sort((a, b) => {
        let aVal, bVal;

        if (sortConfig.key === "scenario") {
          aVal = a.scenario;
          bVal = b.scenario;
        } else if (sortConfig.key === "FIRMWIDE") {
          aVal = a.firmwide_pnl ?? 0;
          bVal = b.firmwide_pnl ?? 0;
        } else {
          aVal = a.by_strategy?.[sortConfig.key]?.pnl ?? 0;
          bVal = b.by_strategy?.[sortConfig.key]?.pnl ?? 0;
        }

        if (typeof aVal === "string") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      });
      return { ungrouped: sorted };
    }

    const sorted = {};
    Object.entries(groupedRows).forEach(([groupKey, groupRowsArray]) => {
      if (!sortConfig.key) {
        sorted[groupKey] = groupRowsArray;
        return;
      }

      sorted[groupKey] = [...groupRowsArray].sort((a, b) => {
        let aVal, bVal;

        if (sortConfig.key === "scenario") {
          aVal = a.scenario;
          bVal = b.scenario;
        } else if (sortConfig.key === "FIRMWIDE") {
          aVal = a.firmwide_pnl ?? 0;
          bVal = b.firmwide_pnl ?? 0;
        } else {
          aVal = a.by_strategy?.[sortConfig.key]?.pnl ?? 0;
          bVal = b.by_strategy?.[sortConfig.key]?.pnl ?? 0;
        }

        if (typeof aVal === "string") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      });
    });

    return sorted;
  }, [groupedRows, sortConfig, groupScenarios, rows]);

  // One scenario row. indent=true puts the label under a factor-group header.
  const renderScenarioRow = (row, indent) => (
            <TableRow
              key={row.sim_number}
              onClick={() => onSimClick?.(row.sim_number, row.firmwide_pnl, row.scenario, row)}
              sx={{
                bgcolor: rowBgColor(row.firmwide_pnl),
                cursor: onSimClick ? "pointer" : "default",
                "&:hover": { bgcolor: "rgba(145, 182, 192, 0.28)" },
                ...ROW_HOVER_SX,
              }}
            >
              <TableCell sx={{ ...cellSx, position: "sticky", left: 0, bgcolor: "transparent", zIndex: 1, ...(indent ? { pl: 3 } : {}) }}>
                <Typography className="scenario-name" sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.15 }}>
                  {row.scenario}
                </Typography>
                <Typography sx={{ fontSize: "0.64rem", color: "text.secondary", lineHeight: 1.1, mt: 0.1 }}>
                  {row.period_or_definition}
                </Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                <Tooltip title={`${formatCurrency(row.firmwide_pnl)} / ${formatBps(row.firmwide_bps)}`} arrow {...VALUE_TOOLTIP_PROPS}>
                  <Box><PnlBpsCell pnl={row.firmwide_pnl} bps={row.firmwide_bps} showBpsOfNav={showBpsOfNav} /></Box>
                </Tooltip>
              </TableCell>
              {strategies.map((s) => {
                const impact = row.by_strategy?.[s];
                return (
                  <TableCell key={s} sx={{ ...cellSx, textAlign: "right" }}>
                    {impact ? (
                      <Tooltip title={`${formatCurrency(impact.pnl)} / ${formatBps(impact.bps)}`} arrow {...VALUE_TOOLTIP_PROPS}>
                        <Box><PnlBpsCell pnl={impact.pnl} bps={impact.bps} showBpsOfNav={showBpsOfNav} /></Box>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: "0.74rem", color: "text.disabled" }}>—</Typography>
                    )}
                  </TableCell>
                );
              })}
              {extraColumns.map((col) => {
                const value = row.extra?.[col];
                const text = value == null || value === ""
                  ? "—"
                  : typeof value === "number"
                    ? formatNumberish(value)
                    : String(value);
                return (
                  <TableCell key={col} sx={{ ...cellSx, textAlign: "right", fontFamily: "monospace" }}>
                    {typeof value === "number" ? (
                      <Tooltip title={formatCurrency(value)} arrow {...VALUE_TOOLTIP_PROPS}>
                        <Box component="span">{text}</Box>
                      </Tooltip>
                    ) : (
                      text
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
  );

  if (!rows || rows.length === 0) {
    return (
      <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
        No scenario data available.
      </Typography>
    );
  }

  const primaryColLabel = singleStrategy || "Firmwide";
  const HEADER_HEIGHT = 48;
  const ROW_HEIGHT = 38;
  const maxHeight = visibleRows ? HEADER_HEIGHT + visibleRows * ROW_HEIGHT : (expanded ? undefined : 600);

  const SortableHeader = ({ label, value, subLabel }) => (
    <Box
      onClick={() => onSort(value)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.3,
        cursor: groupScenarios ? "pointer" : "default",
        userSelect: "none",
        "&:hover": groupScenarios ? { opacity: 0.7 } : {},
      }}
    >
      <Box>
        <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
          {label}
        </Typography>
        {subLabel && (
          <Box sx={{ fontSize: "0.58rem", fontWeight: 500, opacity: 0.7, textTransform: "none", mt: 0.15 }}>
            {subLabel}
          </Box>
        )}
      </Box>
      {groupScenarios && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2, ml: 0.2 }}>
          <ExpandLessIcon sx={{ fontSize: 12, opacity: sortConfig.key === value && sortConfig.direction === "desc" ? 0.8 : 0.3 }} />
          <ExpandMoreIcon sx={{ fontSize: 12, opacity: sortConfig.key === value && sortConfig.direction === "asc" ? 0.8 : 0.3 }} />
        </Box>
      )}
    </Box>
  );

  return (
    <TableContainer sx={maxHeight ? { maxHeight, overflowY: "auto", overflowX: "auto", position: "relative" } : (expanded ? { maxHeight: "calc(100vh - 200px)", overflowY: "auto", overflowX: "auto", position: "relative", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } } : { maxHeight: 600, position: "relative" })}>
      <Table size="small" stickyHeader sx={{ "& thead th": { position: "sticky", top: 0, zIndex: 10, bgcolor: "background.paper" } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerSx, minWidth: 180, position: "sticky", left: 0, zIndex: 3, bgcolor: "background.paper", lineHeight: 1.2 }}>
              <Box>
                <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
                  Scenario
                </Typography>
                <Box sx={{ fontSize: "0.58rem", fontWeight: 500, opacity: 0.7, textTransform: "none", mt: 0.15 }}>
                  Stress Period
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ ...headerSx, textAlign: "right", minWidth: 100, lineHeight: 1.2 }}>
              <SortableHeader
                label={primaryColLabel}
                value="FIRMWIDE"
                subLabel={showBpsOfNav ? "bps of NAV" : undefined}
              />
            </TableCell>
            {strategies.map((s) => (
              <TableCell key={s} sx={{ ...headerSx, textAlign: "right", minWidth: 90 }}>
                <Tooltip title={s} arrow>
                  <Box
                    onClick={() => onSort(s)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.3,
                      cursor: groupScenarios ? "pointer" : "default",
                      userSelect: "none",
                      justifyContent: "flex-end",
                      "&:hover": groupScenarios ? { opacity: 0.7 } : {},
                    }}
                  >
                    <span>{STRATEGY_SHORT[s] || s}</span>
                    {groupScenarios && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                        <ExpandLessIcon sx={{ fontSize: 10, opacity: sortConfig.key === s && sortConfig.direction === "desc" ? 0.8 : 0.3 }} />
                        <ExpandMoreIcon sx={{ fontSize: 10, opacity: sortConfig.key === s && sortConfig.direction === "asc" ? 0.8 : 0.3 }} />
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              </TableCell>
            ))}
            {extraColumns.map((col) => (
              <TableCell key={col} sx={{ ...headerSx, textAlign: "right", minWidth: 90 }}>
                <Box
                  onClick={() => onSort(col)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.3,
                    cursor: groupScenarios ? "pointer" : "default",
                    userSelect: "none",
                    justifyContent: "flex-end",
                    "&:hover": groupScenarios ? { opacity: 0.7 } : {},
                  }}
                >
                  <span>{String(col).replace(/_/g, " ")}</span>
                  {groupScenarios && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                      <ExpandLessIcon sx={{ fontSize: 10, opacity: sortConfig.key === col && sortConfig.direction === "desc" ? 0.8 : 0.3 }} />
                      <ExpandMoreIcon sx={{ fontSize: 10, opacity: sortConfig.key === col && sortConfig.direction === "asc" ? 0.8 : 0.3 }} />
                    </Box>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groupScenarios ? (
            <>
            {pinnedRows.map((row) => renderScenarioRow(row, false))}
            {Object.entries(sortedGroupedRows).map(([groupKey, groupRowsArray]) => {
              if (!groupRowsArray || groupRowsArray.length === 0) return null;

              const isExpanded = expandedGroups[groupKey] ?? true;
              const groupLabel = groupLabelByKey[groupKey] || groupKey.replace(/_/g, " ");

              return (
                <React.Fragment key={groupKey}>
                  <TableRow sx={{ bgcolor: "rgba(0, 104, 66, 0.06)", "&:hover": { bgcolor: "rgba(0, 104, 66, 0.1)" } }}>
                    <TableCell
                      colSpan={strategies.length + extraColumns.length + 2}
                      sx={{ ...cellSx, py: 0.75, cursor: "pointer", userSelect: "none" }}
                      onClick={() => onToggleGroup(groupKey)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#006842" }}>
                          {groupLabel}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", ml: "auto" }}>
                          ({groupRowsArray.length} scenario{groupRowsArray.length !== 1 ? "s" : ""})
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {isExpanded && groupRowsArray.map((row) => renderScenarioRow(row, true))}
                </React.Fragment>
              );
            })}
            </>
          ) : (
            sortedGroupedRows.ungrouped.map((row) => (
              <TableRow
                key={row.sim_number}
                onClick={() => onSimClick?.(row.sim_number, row.firmwide_pnl, row.scenario, row)}
                sx={{
                  bgcolor: rowBgColor(row.firmwide_pnl),
                  cursor: onSimClick ? "pointer" : "default",
                  "&:hover": { bgcolor: "rgba(145, 182, 192, 0.28)" },
                  ...ROW_HOVER_SX,
                }}
              >
                <TableCell sx={{ ...cellSx, position: "sticky", left: 0, bgcolor: "transparent", zIndex: 1 }}>
                  <Typography className="scenario-name" sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.15 }}>
                    {row.scenario}
                  </Typography>
                  <Typography sx={{ fontSize: "0.64rem", color: "text.secondary", lineHeight: 1.1, mt: 0.1 }}>
                    {row.period_or_definition}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                  <Tooltip title={`${formatCurrency(row.firmwide_pnl)} / ${formatBps(row.firmwide_bps)}`} arrow {...VALUE_TOOLTIP_PROPS}>
                    <Box><PnlBpsCell pnl={row.firmwide_pnl} bps={row.firmwide_bps} showBpsOfNav={showBpsOfNav} /></Box>
                  </Tooltip>
                </TableCell>
                {strategies.map((s) => {
                  const impact = row.by_strategy?.[s];
                  return (
                    <TableCell key={s} sx={{ ...cellSx, textAlign: "right" }}>
                      {impact ? (
                        <Tooltip title={`${formatCurrency(impact.pnl)} / ${formatBps(impact.bps)}`} arrow {...VALUE_TOOLTIP_PROPS}>
                          <Box><PnlBpsCell pnl={impact.pnl} bps={impact.bps} showBpsOfNav={showBpsOfNav} /></Box>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: "0.74rem", color: "text.disabled" }}>—</Typography>
                      )}
                    </TableCell>
                  );
                })}
                {extraColumns.map((col) => {
                  const value = row.extra?.[col];
                  const text = value == null || value === ""
                    ? "—"
                    : typeof value === "number"
                      ? formatNumberish(value)
                      : String(value);
                  return (
                    <TableCell key={col} sx={{ ...cellSx, textAlign: "right", fontFamily: "monospace" }}>
                      {typeof value === "number" ? (
                        <Tooltip title={formatCurrency(value)} arrow {...VALUE_TOOLTIP_PROPS}>
                          <Box component="span">{text}</Box>
                        </Tooltip>
                      ) : (
                        text
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function defValueColor(v) {
  if (typeof v !== "string") return "inherit";
  return v.startsWith("-") ? "#d57575" : "inherit";
}


function DefinitionsTab() {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" }, gap: { xs: 1.5, md: 2 }, alignItems: "start" }}>
      <Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Scenario", "Period", "SPX", "VIX", "IG bps", "HY bps", "2Y bps"].map((h) => (
                  <TableCell key={h} sx={headerSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {HIST_DEFINITIONS.map((d) => (
                <TableRow key={d.scenario}>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{d.scenario}</TableCell>
                  <TableCell sx={{ ...cellSx, color: "text.secondary", fontSize: "0.72rem" }}>{d.period}</TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: "monospace", color: defValueColor(d.spx) }}>{d.spx}</TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: "monospace", color: defValueColor(d.vix) }}>{d.vix}</TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: "monospace", color: defValueColor(d.ig) }}>{d.ig}</TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: "monospace", color: defValueColor(d.hy) }}>{d.hy}</TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: "monospace", color: defValueColor(d.twoY) }}>{d.twoY}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Scenario", "Factor Shocks"].map((h) => (
                  <TableCell key={h} sx={headerSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {CUSTOM_DEFINITIONS.map((d) => (
                <TableRow key={d.scenario}>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{d.scenario}</TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: "monospace", letterSpacing: "0.02em" }}>{d.factors}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

function SectionHeader({ title, right }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "#BE9B53" }}>
        {title}
      </Typography>
      {right}
    </Box>
  );
}

export default function StressScenarioPanel({ data, loading, onSimClick, singleStrategy, beforeDefinitions, showBpsOfNav = false, customOnly = false }) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: 2, bgcolor: "background.paper" }}>
            <Skeleton variant="text" width={240} height={28} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 1, borderRadius: 1 }} />
          </Box>
        ))}
      </Box>
    );
  }

  const hasData = data && (data.historical?.length > 0 || data.custom?.length > 0);

  const gmvBadge = data?.gross_market_value > 0 ? (
    <Tooltip title={`Gross Market Value: $${data.gross_market_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} arrow>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#006842", fontFamily: "monospace", cursor: "help" }}>
        GMV: {formatGmv(data.gross_market_value)}
      </Typography>
    </Tooltip>
  ) : null;

  if (customOnly) {
    return (
      <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: { xs: 1, sm: 2 }, bgcolor: "background.paper", minWidth: 0, overflow: "hidden" }}>
        <SectionHeader title="Custom Shocks" right={gmvBadge} />
        {data?.custom?.length > 0 ? (
          <ScenarioTable
            rows={data.custom}
            onSimClick={onSimClick}
            singleStrategy={null}
            showBpsOfNav={showBpsOfNav}
            expanded
          />
        ) : (
          <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
            No custom shock data available for this date.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Historical & Custom Shocks — side by side */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: { xs: 1.5, lg: 2 } }}>
        <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: { xs: 1, sm: 2 }, bgcolor: "background.paper", minWidth: 0, overflowX: "auto", overflowY: "hidden" }}>
          <SectionHeader title="Historical Shocks" />
          {hasData ? (
            <ScenarioTable rows={data?.historical} onSimClick={onSimClick} singleStrategy={singleStrategy} showBpsOfNav={showBpsOfNav} />
          ) : (
            <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
              No historical shock data available for this date.
            </Typography>
          )}
        </Box>

        <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: { xs: 1, sm: 2 }, bgcolor: "background.paper", minWidth: 0, overflowX: "auto", overflowY: "hidden" }}>
          <SectionHeader title="Custom Shocks" />
          {hasData ? (
            <ScenarioTable rows={data?.custom} onSimClick={onSimClick} singleStrategy={singleStrategy} showBpsOfNav={showBpsOfNav} visibleRows={6} />
          ) : (
            <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
              No custom shock data available for this date.
            </Typography>
          )}
        </Box>
      </Box>

      {beforeDefinitions}

      {/* Scenario Definitions */}
      <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: { xs: 1, sm: 2 }, bgcolor: "background.paper", minWidth: 0, overflow: "hidden" }}>
        <SectionHeader title="SCENARIO DEFINITIONS & DISCLOSURES" />
        <DefinitionsTab />
      </Box>
    </Box>
  );
}
